import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { Flame, Search, X } from 'lucide-react'
import { cmsApi } from '../lib/api'
import { formatWeekday } from '../lib/format'
import type { PopularLineup, PopularOverview, PopularSection } from '../types'
import { LoadingState } from './LoadingState'
import { useDragOrder } from './dragOrder'

type Props = {
  canEdit: boolean
  // The city it opens on, from the Visão geral.
  initialCity?: string
  onSaved: (message: string) => void
  onError: (message: string) => void
}

// The name, and below it in the search what tells it apart; search adds
// words to find it by (a profile's @); tag, an event's state beside it.
// until: when the event last plays in the city (PRD.md, item 101).
type Item = { id: string; label: string; detail?: string; search?: string; tag?: { label: string; active: boolean }; note?: string; until?: string }

const SECTION_TITLES: Record<PopularSection, string> = {
  top: 'Top 3',
  film: 'Vale o ingresso',
  profiles: 'Para você seguir',
}

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`

// Why a Top 3 pick is not on the app now.
const TOP_NOTES = {
  active: undefined,
  ended: 'Encerrado',
  no_photo: 'Sem foto',
} as const

// "15/10", in Brasília time.
const dayOf = (value: string) => new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit', month: '2-digit', timeZone: 'America/Sao_Paulo',
}).format(new Date(value))

// What keeps a city out of the app: the sections it lacks.
const missingOf = (overview: PopularOverview) => [
  overview.live.top.length < 3 && SECTION_TITLES.top,
  !overview.live.film && SECTION_TITLES.film,
  overview.live.profiles.length < 4 && SECTION_TITLES.profiles,
].filter((title): title is string => Boolean(title))

export function PopularPage({ canEdit, initialCity, onSaved, onError }: Props) {
  // Empty until the editor picks one: the API opens on the first city that qualifies.
  const [city, setCity] = useState(initialCity ?? '')
  const [overview, setOverview] = useState<PopularOverview | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      setOverview(await cmsApi.getPopular(city))
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Não foi possível carregar o Popular.')
    } finally {
      setLoading(false)
    }
  }, [city])

  useEffect(() => { void load() }, [load])

  // Every city at once, the calendar of the next swaps (PRD.md, item 101).
  const [lineup, setLineup] = useState<PopularLineup[] | null>(null)
  const loadLineup = useCallback(() => cmsApi.popularLineup().then(setLineup).catch(() => setLineup(null)), [])
  useEffect(() => { void loadLineup() }, [loadLineup])

  const save = (section: PopularSection) => async (ids: string[]) => {
    if (!overview) return
    try {
      setOverview(await cmsApi.savePopularPicks(overview.city, section, ids))
      void loadLineup()
      onSaved(`${SECTION_TITLES[section]} atualizado.`)
    } catch (requestError) {
      onError(requestError instanceof Error ? requestError.message : 'Não foi possível salvar.')
    }
  }

  const eligible = overview?.cities.find((item) => item.city === overview.city)?.eligible ?? false
  const missing = overview ? missingOf(overview) : []

  return (
    <div className="page-stack">
      <section className="page-heading">
        <h1>Popular</h1>
        <p>O que a equipe escolhe no Popular de cada cidade. A escolha da equipe vem antes da regra automática.</p>
      </section>

      {lineup && lineup.length > 0 && (
        <section className="panel">
          <div className="panel__heading">
            <div>
              <h2>Próximas trocas</h2>
              <p>Cada cidade como o app mostra agora: o primeiro do Top 3 a sair e até quando vai o filme. A semana nova de cinema sai na terça ou na quarta.</p>
            </div>
          </div>
          <div className="swap-list">
            {lineup.map((item) => {
              const next = item.top.find((event) => event.last_session)
              return (
                <button key={item.city} className={`swap-row${item.city === overview?.city ? ' is-current' : ''}`} onClick={() => setCity(item.city)}>
                  <strong>{item.city}</strong>
                  <span className={`status-pill ${item.missing.length ? 'status-pill--canceled' : 'status-pill--published'}`}>{item.missing.length ? 'Fora do app' : 'No app'}</span>
                  <span className="swap-row__detail">{item.missing.length
                    ? `Falta ${item.missing.map((section) => SECTION_TITLES[section]).join(', ')}`
                    : next?.last_session ? `Sai primeiro: ${next.title}, ${formatWeekday(next.last_session)}` : 'Top 3 sem data de fim'}</span>
                  <span className="swap-row__detail">{item.film
                    ? `Vale o ingresso: ${item.film.title}${item.film.last_session ? `, até ${formatWeekday(item.film.last_session)}` : ''}`
                    : 'Sem filme'}</span>
                </button>
              )
            })}
          </div>
        </section>
      )}

      <section className="panel">
        <div className="filter-bar filter-bar--popular">
          <select aria-label="Cidade" value={city || overview?.city || ''} disabled={!overview} onChange={(event) => setCity(event.target.value)}>
            {overview?.cities.map((item) => <option key={item.city} value={item.city}>{item.city}</option>)}
          </select>
          {overview && (
            <div className="popular-city-status">
              <span className={`status-pill ${eligible ? 'status-pill--published' : 'status-pill--canceled'}`}>{eligible ? 'No app' : 'Fora do app'}</span>
              <span>{eligible
                ? `O app mostra Popular em ${overview.city}.`
                : `Falta ${missing.join(', ')}. Quem está nesta cidade vê o Popular da cidade mais próxima que tem as três seções.`}</span>
            </div>
          )}
        </div>

        {loading && !overview ? <LoadingState label="Carregando o Popular…" /> : error ? (
          <div className="error-state"><p>{error}</p><button className="button button--secondary" onClick={() => void load()}>Tentar novamente</button></div>
        ) : overview ? (
          <div className="place-list">
            <PicksCard
              key={`${overview.city}-top-${overview.picks.top.map((pick) => pick.id).join()}`}
              title={SECTION_TITLES.top}
              hint="Até 3 eventos fora do cinema, acontecendo ou que ainda vão começar. A escolha da equipe entra na hora e sai quando o evento termina; a regra completa com os mais favoritados da semana."
              limit={3}
              addLabel="Buscar evento…"
              saved={overview.picks.top.map((pick) => ({ id: pick.id, label: pick.title, note: TOP_NOTES[pick.status],
                until: pick.status === 'active' && pick.last_session ? `Até ${formatWeekday(pick.last_session)}` : undefined }))}
              options={overview.options.events.map((item) => ({
                id: item.id,
                label: item.title,
                detail: [item.category, item.last_session && `até ${formatWeekday(item.last_session)}`].filter(Boolean).join(' · ') || undefined,
                tag: item.happening ? { label: 'Acontecendo', active: true } : { label: `Começa ${dayOf(item.starts_at)}`, active: false },
              }))}
              live={overview.live.top.map((item) => ({ label: item.title, team: item.source === 'pick',
                until: item.last_session ? `até ${formatWeekday(item.last_session)}` : undefined }))}
              emptyLive="Nenhum evento com favoritos nesta semana."
              emptyPicks="Nenhuma escolha: vale a regra."
              canEdit={canEdit}
              onSave={save('top')}
            />
            <PicksCard
              key={`${overview.city}-film-${overview.picks.film?.id ?? ''}`}
              title={SECTION_TITLES.film}
              hint="Um filme para todas as cidades, escolhido só pela equipe. Em cada cidade, aparece com os cinemas de lá enquanto tiver sessões."
              limit={1}
              slotLabel="Filme"
              addLabel="Buscar filme…"
              saved={overview.picks.film ? [{ id: overview.picks.film.id, label: overview.picks.film.title, note: overview.picks.film.active ? undefined : `Sem sessões em ${overview.city}`,
                until: overview.picks.film.active && overview.picks.film.last_session ? `Sessões até ${formatWeekday(overview.picks.film.last_session)}` : undefined }] : []}
              options={overview.options.films.map((item) => ({
                id: item.id,
                label: item.title,
                detail: `${plural(item.sessions, 'sessão', 'sessões')} · ${item.cities.join(', ')}`,
              }))}
              live={overview.live.film ? [{ label: overview.live.film.title, team: true,
                until: overview.live.film.last_session ? `sessões até ${formatWeekday(overview.live.film.last_session)}` : undefined }] : []}
              emptyLive={overview.picks.film ? `Sem sessões em ${overview.city}: a seção não aparece aqui.` : 'Sem filme: a seção não aparece.'}
              emptyPicks="Nenhum filme escolhido."
              canEdit={canEdit}
              onSave={save('film')}
            />
            <PicksCard
              key={`${overview.city}-profiles-${overview.picks.profiles.map((pick) => pick.id).join()}`}
              title={SECTION_TITLES.profiles}
              hint="De 4 a 20 perfis. A escolha da equipe vem primeiro; a regra completa com perfis com eventos na cidade, por seguidores e eventos, alternando categorias. Quem a pessoa já segue não aparece."
              limit={20}
              addLabel="Buscar perfil…"
              saved={overview.picks.profiles.map((pick) => ({ id: pick.id, label: pick.name, note: pick.image_url ? undefined : 'Sem foto' }))}
              options={overview.options.profiles.map((item) => ({
                id: item.id,
                label: item.name,
                detail: [item.handle && `@${item.handle}`, plural(item.followers, 'seguidor', 'seguidores'),
                  plural(item.events, 'evento', 'eventos'), !item.image_url && 'sem foto'].filter(Boolean).join(' · '),
                search: `${item.name} ${item.handle ?? ''}`,
              }))}
              live={overview.live.profiles.map((item) => ({ label: item.name, team: item.source === 'pick' }))}
              emptyLive="Menos de 4 perfis: a seção não aparece."
              emptyPicks="Nenhuma escolha: vale a regra."
              canEdit={canEdit}
              onSave={save('profiles')}
            />
          </div>
        ) : (
          <div className="empty-state"><Flame size={30} /><h3>Nenhuma cidade com eventos</h3></div>
        )}
      </section>
    </div>
  )
}

type CardProps = {
  title: string
  hint: string
  limit: number
  addLabel: string
  // The one place of a single pick ("Filme"); a few picks are "1º lugar"…
  slotLabel?: string
  saved: Item[]
  options: Item[]
  live: { label: string; team: boolean; until?: string }[]
  emptyLive: string
  emptyPicks: string
  canEdit: boolean
  onSave: (ids: string[]) => Promise<void>
}

// One section: what the app shows now, then the team's picks in order, saved
// together (PRD.md, items 25 and 26). Up to three picks are places to fill in
// order, each offering what no other place has; a longer list is rows, and a
// box to add. Both reorder by dragging their handle, or with the arrow keys
// on it, as Trello and GitHub do.
function PicksCard({ title, hint, limit, addLabel, slotLabel, saved, options, live, emptyLive, emptyPicks, canEdit, onSave }: CardProps) {
  const [draft, setDraft] = useState<string[]>(saved.map((item) => item.id))
  const [saving, setSaving] = useState(false)
  const changed = draft.join() !== saved.map((item) => item.id).join()
  const labelOf = (id: string) => saved.find((item) => item.id === id) ?? options.find((item) => item.id === id) ?? { id, label: 'Removido' }
  // The picks no longer offered (ended, without a photo) stay choosable where they are.
  const choices = [...saved.filter((item) => !options.some((option) => option.id === item.id)), ...options]
  const placeOf = (index: number) => (limit > 1 ? `${index + 1}º lugar` : slotLabel ?? title)

  const choose = (index: number, id: string) => setDraft((current) => {
    const next = [...current]
    if (index < next.length) next[index] = id
    else next.push(id)
    return next
  })
  const remove = (id: string) => setDraft((current) => current.filter((value) => value !== id))
  const reorder = (from: number, to: number) => setDraft((current) => {
    const next = [...current]
    const [id] = next.splice(from, 1)
    next.splice(to, 0, id)
    return next
  })

  // A filled row takes another dropped on it.
  const { dropTarget, handle: dragHandle } = useDragOrder(draft.length, reorder, saving)
  const handle = (index: number, label: string) => (canEdit ? dragHandle(index, label) : <span />)

  const submit = async () => {
    setSaving(true)
    try {
      await onSave(draft)
    } finally {
      setSaving(false)
    }
  }

  return (
    <article className="place-card" aria-label={title}>
      <header className="place-card__heading">
        <div>
          <strong>{title}</strong>
          <span>{hint}</span>
        </div>
      </header>

      <div className="amenity-group">
        <h3>No app agora</h3>
        {live.length ? (
          <ol className="popular-live">
            {live.map((item, index) => (
              <li key={`${item.label}-${index}`}>
                {limit > 1 && <span className="popular-live__position">{index + 1}</span>}
                <span>{item.label}</span>
                {item.until && <small className="popular-live__until">{item.until}</small>}
                <span className={`status-pill ${item.team ? 'status-pill--published' : 'status-pill--deleted'}`}>{item.team ? 'Equipe' : 'Automático'}</span>
              </li>
            ))}
          </ol>
        ) : <p className="popular-empty">{emptyLive}</p>}
      </div>

      <div className="amenity-group">
        <h3>Escolhas da equipe</h3>
        {limit <= 3 ? (
          <div className="pick-slots" role="list" aria-label={`Escolhas da equipe em ${title}`}>
            {Array.from({ length: limit }, (_, index) => {
              const id = draft[index]
              const item = id ? labelOf(id) : null
              // Filled in order: the next empty place opens after the last one filled.
              const open = index <= draft.length
              const row = item && limit > 1 ? dropTarget(index) : { className: undefined }
              return (
                <div {...row} className={['pick-slot', limit > 1 && 'pick-slot--ordered', row.className].filter(Boolean).join(' ')} role="listitem" key={id ?? `empty-${index}`}>
                  {limit > 1 && (item ? handle(index, item.label) : <span />)}
                  <span className="pick-slot__label">{placeOf(index)}</span>
                  <PickSearch label={`${placeOf(index)} em ${title}`} placeholder={open ? addLabel : 'Escolha o anterior primeiro'}
                    value={item} options={choices.filter((choice) => choice.id === id || !draft.includes(choice.id))}
                    disabled={!canEdit || saving || !open} onChoose={(choiceId) => choose(index, choiceId)} />
                  {canEdit && item
                    ? <button type="button" className="icon-button" aria-label={`Tirar ${item.label}`} disabled={saving} onClick={() => remove(item.id)}><X size={15} /></button>
                    : <span />}
                  {item?.note && <small className="pick-slot__note">{item.note}</small>}
                  {item?.until && <small className="pick-slot__until">{item.until}</small>}
                </div>
              )
            })}
            {!draft.length && !canEdit && <p className="popular-empty">{emptyPicks}</p>}
          </div>
        ) : (
          <>
            {draft.length > 0 && (
              <ol className="pick-list" aria-label={`Escolhas da equipe em ${title}`}>
                {draft.map((id, index) => {
                  const item = labelOf(id)
                  return (
                    <li {...dropTarget(index)} key={id}>
                      {handle(index, item.label)}
                      <span className="pick-list__position">{index + 1}</span>
                      <span className="pick-list__label">{item.label}{item.note && <small className="popular-chip-note">{item.note}</small>}</span>
                      {canEdit && <button type="button" className="icon-button" aria-label={`Tirar ${item.label}`} disabled={saving} onClick={() => remove(id)}><X size={15} /></button>}
                    </li>
                  )
                })}
              </ol>
            )}
            {canEdit && draft.length < limit && (
              <PickSearch className="pick-search--add" label={`Adicionar a ${title}`} placeholder={addLabel} value={null}
                options={options.filter((item) => !draft.includes(item.id))} disabled={saving}
                onChoose={(id) => setDraft((current) => [...current, id])} />
            )}
            {!draft.length && !canEdit && <p className="popular-empty">{emptyPicks}</p>}
          </>
        )}
      </div>

      {canEdit && changed && (
        <footer className="place-card__footer">
          <span>Alterações sem salvar</span>
          <div>
            <button className="button button--quiet" disabled={saving} onClick={() => setDraft(saved.map((item) => item.id))}>Desfazer</button>
            <button className="button button--primary" disabled={saving} onClick={() => void submit()}>{saving ? 'Salvando…' : 'Salvar'}</button>
          </div>
        </footer>
      )}
    </article>
  )
}

// "museu amanha" finds "Museu do Amanhã": no accents or case, the words in
// any order, as the Eventos page searches (PRD.md, items 9 and 29).
const plain = (value: string) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
const matches = (item: Item, words: string[]) => {
  const text = plain(item.search ?? item.label)
  return words.every((word) => text.includes(word))
}

type SearchProps = {
  label: string
  placeholder: string
  value: Item | null
  options: Item[]
  disabled?: boolean
  className?: string
  onChoose: (id: string) => void
}

// A box that searches its options as one types (PRD.md, item 29), as Notion
// and Linear pick from long lists: the list opens on focus, in the given
// order, and narrows with each word; the arrows move, Enter chooses, Esc
// closes. Closed, it shows the choice.
function PickSearch({ label, placeholder, value, options, disabled, className, onChoose }: SearchProps) {
  const listId = useId()
  const listRef = useRef<HTMLUListElement>(null)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const results = useMemo(() => {
    const words = plain(query).split(/\s+/).filter(Boolean)
    return options.filter((item) => matches(item, words))
  }, [options, query])

  useEffect(() => {
    if (open) listRef.current?.children[active]?.scrollIntoView({ block: 'nearest' })
  }, [open, active])

  const show = () => {
    setOpen(true)
    setActive(0)
  }
  const close = () => {
    setOpen(false)
    setQuery('')
  }
  const choose = (item: Item) => {
    onChoose(item.id)
    close()
  }

  return (
    <div className={['pick-search', className].filter(Boolean).join(' ')}>
      <div className={`search-field ${disabled ? 'search-field--disabled' : ''}`}>
        <Search size={16} />
        <input role="combobox" aria-label={label} aria-expanded={open} aria-controls={listId} aria-autocomplete="list"
          aria-activedescendant={open && results[active] ? `${listId}-${active}` : undefined}
          value={open ? query : value?.label ?? ''} placeholder={open && value ? value.label : placeholder} disabled={disabled}
          onFocus={show} onClick={() => !open && show()} onBlur={close}
          onChange={(event) => {
            setQuery(event.target.value)
            setActive(0)
            setOpen(true)
          }}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              event.preventDefault()
              if (!open) return show()
              const step = event.key === 'ArrowDown' ? 1 : -1
              setActive((current) => Math.min(Math.max(current + step, 0), results.length - 1))
            } else if (event.key === 'Enter' && open && results[active]) {
              event.preventDefault()
              choose(results[active])
            } else if (event.key === 'Escape' && open) {
              event.preventDefault()
              close()
            }
          }} />
      </div>
      {open && (
        <ul className="pick-search__list" id={listId} role="listbox" ref={listRef}>
          {results.map((item, index) => (
            <li key={item.id} id={`${listId}-${index}`} role="option" aria-selected={index === active}
              onMouseDown={(event) => {
                event.preventDefault()
                choose(item)
              }}
              onMouseMove={() => setActive(index)}>
              <span className="pick-search__text">
                <span>{item.label}</span>
                {item.detail && <small>{item.detail}</small>}
              </span>
              {item.tag && <span className={`status-pill ${item.tag.active ? 'status-pill--published' : 'status-pill--deleted'}`}>{item.tag.label}</span>}
            </li>
          ))}
          {!results.length && <li className="pick-search__empty" role="presentation">Nenhum resultado.</li>}
        </ul>
      )}
    </div>
  )
}
